import { Separator } from '@/components/ui/separator'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { GameRoomDescriptionDisplay } from '@/components/scenario/GameRoomDescription'
import { EventCollapsibleRow } from './EventCollapsibleRow'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { useGamePhase } from '@/hooks/useGamePhase'
import { Button } from '@/components/ui/button'
import { UserRole } from '@/constants/room'
import { useParams } from 'react-router-dom'
import { SendDecisionModal } from '@/components/Student/SendDecisionModal'
import { ModalMode } from '@/components/Student/types'
import { submitStudentDecision } from '@/redux/asyncThunks/studentDecisionWebsocket'
import { getRoomDetails } from '@/redux/asyncThunks/room'
import { fetchTimerConfigurationAPIThunk } from '@/redux/asyncThunks/timerConfiguration'
import { GameroomEvent, GameRoomStatus } from '@/redux/types/room'
import { findUserDecision } from '@/lib/utils'
import { ActivityState } from '@/redux/slices/timerConfigurationSlice'
import { useEffectiveUser } from '@/hooks/useEffectiveUser'
import PlayerResourceLimits from '@/components/Student/PlayerResourceLimits'
import {
  groupEventsByRound,
  eventShowsPhaseLabel,
  buildScheduledReleaseRoundByEventId,
  UNRELEASED_GROUP_KEY,
} from '@/utils/groupEventsByRound'
import { ActiveTab, isCharterTab } from '@/constants/chat'
import { CharterPrepSidebar } from '@/features/charter/components/Shell/CharterPrepSidebar'
import { CharterGroupSidebar } from '@/features/charter/components/Shell/CharterGroupSidebar'

interface OGESidebarContentProps {
  openEvents: { [key: number]: boolean }
  onEventToggle: (eventId: number, isOpen: boolean) => void
  openRounds: Record<number, boolean>
  onRoundToggle: (roundKey: number, isOpen: boolean) => void
}

const OGESidebarContent: React.FC<OGESidebarContentProps> = ({
  openEvents,
  onEventToggle,
  openRounds,
  onRoundToggle,
}) => {
  const params = useParams()
  const dispatch = useAppDispatch()
  const roomId = params.roomId as string
  const [modalState, setModalState] = useState<{
    isOpen: boolean
    mode: ModalMode
  }>({
    isOpen: false,
    mode: ModalMode.SUBMIT,
  })
  const [isPolling, setIsPolling] = useState<boolean>(false)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const POLLING_INTERVAL = 5000

  const { decisions } = useAppSelector((state) => state.decisionsWS)
  const { user } = useEffectiveUser()
  const { currentRoom } = useAppSelector((state) => state.room)
  const roomMembers = useAppSelector((state) => state.room.roomMembers)
  const timerConfig = useAppSelector((state) => state.timerConfiguration.config)
  const activeTab = useAppSelector((state) => state.chat.activeTab)

  const isProfessor = user?.role === UserRole.Professor
  const { roundStatus, decisionPhase, manualMode, currentRound, gameMode } =
    useGamePhase()

  const myDecision = useMemo(
    () => findUserDecision(decisions, user?.id, decisionPhase?.id),
    [decisions, user?.id, decisionPhase?.id]
  )

  const myTeamMemberIds = useMemo(() => {
    if (!user?.email) return new Set<number>()

    const currentMember = roomMembers.find(
      (member) => member.member_email === user.email
    )
    if (!currentMember?.teamId) return new Set<number>()

    return new Set(
      roomMembers
        .filter((member) => member.teamId === currentMember.teamId)
        .map((member) => Number(member.member_id))
        .filter((memberId) => Number.isFinite(memberId))
    )
  }, [roomMembers, user?.email])

  useEffect(() => {
    if (isProfessor && roomId) {
      dispatch(fetchTimerConfigurationAPIThunk(roomId))
    }
  }, [dispatch, isProfessor, roomId])

  const scheduledReleaseByEventId = useMemo(
    () => buildScheduledReleaseRoundByEventId(timerConfig?.roundConfigurations),
    [timerConfig?.roundConfigurations]
  )

  const pastDecisions = useMemo(() => {
    const visibleStudentIds =
      myTeamMemberIds.size > 0
        ? myTeamMemberIds
        : new Set<number>(user?.id ? [user.id] : [])

    return (
      decisions
        ?.filter(
          (decision) =>
            visibleStudentIds.has(decision.student_id) &&
            decision.round_number != null &&
            decision.round_number < currentRound
        )
        .sort((a, b) => {
          const roundDiff = (b.round_number || 0) - (a.round_number || 0)
          if (roundDiff !== 0) return roundDiff

          const nameA = (a.user_name || a.user || '').toLowerCase()
          const nameB = (b.user_name || b.user || '').toLowerCase()
          return nameA.localeCompare(nameB)
        }) || []
    )
  }, [decisions, user?.id, currentRound, myTeamMemberIds])

  const fetchRoomData = useCallback(
    async (isPollingCall = false) => {
      if (roomId) {
        try {
          await dispatch(getRoomDetails(roomId))
        } catch (error) {
          console.error(
            `Failed to ${isPollingCall ? 'poll' : 'fetch'} room data:`,
            error
          )
        }
      }
    },
    [dispatch, roomId]
  )

  useEffect(() => {
    if (!currentRoom || isProfessor) return

    const shouldPoll =
      currentRoom.status === GameRoomStatus.IN_PROGRESS && !modalState.isOpen

    if (shouldPoll && !pollingIntervalRef.current) {
      setIsPolling(true)
      pollingIntervalRef.current = setInterval(() => {
        fetchRoomData(true)
      }, POLLING_INTERVAL)
      console.log('OGE Sidebar polling started')
    }

    if (!shouldPoll && pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
      setIsPolling(false)
      console.log('OGE Sidebar polling stopped - Game ended or not in progress')
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
        setIsPolling(false)
        console.log('OGE Sidebar polling cleaned up')
      }
    }
  }, [currentRoom, fetchRoomData, modalState.isOpen, isProfessor])

  const handleRefresh = () => {
    fetchRoomData(false)
  }


  const openModal = (mode: ModalMode) => {
    setModalState({ isOpen: true, mode })
  }

  const closeModal = () => {
    setModalState({ isOpen: false, mode: ModalMode.SUBMIT })
  }

  const handleSendDecision = useCallback(
    (decision: string, isLate: boolean, isPublic: boolean) => {
      if (decision && decisionPhase) {
        dispatch(
          submitStudentDecision({
            studentId: user?.id || null,
            roomId,
            decision,
            lateSubmission: isLate,
            phaseId: decisionPhase.id,
            isPublic: isPublic,
          })
        )
      }
    },
    [decisionPhase, user?.id, roomId, dispatch]
  )

  const getButtonText = () => {
    if (myDecision?.adjudication) {
      return 'View Adjudication'
    }
    if (myDecision?.decision) {
      return 'View Decision'
    }
    return 'Send Decision'
  }

  const getButtonMode = (): ModalMode => {
    if (myDecision?.decision || myDecision?.adjudication) {
      return ModalMode.VIEW
    }
    return ModalMode.SUBMIT
  }

  const allEvents = currentRoom?.events ?? []

  const eventGroupingOptions = useMemo(
    () => ({
      includeUnreleased: isProfessor,
      scheduledReleaseByEventId: isProfessor
        ? scheduledReleaseByEventId
        : undefined,
    }),
    [isProfessor, scheduledReleaseByEventId]
  )

  const { unreleased, roundGroups } = useMemo(
    () => groupEventsByRound(allEvents, eventGroupingOptions),
    [allEvents, eventGroupingOptions]
  )

  const eventIndexById = useMemo(() => {
    const map = new Map<number, number>()
    allEvents.forEach((event, index) => map.set(event.id, index))
    return map
  }, [allEvents])

  const hasVisibleEvents =
    unreleased.length > 0 || roundGroups.some((g) => g.events.length > 0)

  // Charter channel tabs swap the Operations panel to their own content.
  if (!isProfessor && isCharterTab(activeTab)) {
    return activeTab === ActiveTab.CharterPrep ? (
      <CharterPrepSidebar />
    ) : (
      <CharterGroupSidebar roomId={roomId} />
    )
  }

  const renderEventList = (
    events: GameroomEvent[],
    showPhaseLabelForEvent: (event: GameroomEvent) => boolean = () => false
  ) =>
    events.map((event) => (
      <EventCollapsibleRow
        key={event.id}
        event={event}
        isOpen={openEvents[event.id] ?? false}
        onToggle={(isOpen) => onEventToggle(event.id, isOpen)}
        allEvents={allEvents}
        eventIndex={eventIndexById.get(event.id) ?? 0}
        showPhaseLabel={showPhaseLabelForEvent(event)}
        gameMode={gameMode}
      />
    ))

  return (
    <>
      <div className='flex justify-between items-center px-1'>
        <h1 className='text-dark-blue text-lg font-bold leading-snug'>
          {currentRoom?.scenario_title}
        </h1>
        <div
          onClick={handleRefresh}
          className='relative p-1.5 rounded-lg cursor-pointer bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 flex-shrink-0'
          title={isPolling ? 'Auto-refreshing active' : 'Refresh'}
        >
          <img src='/icons/refresh.svg' alt='Refresh' className='w-3 h-3' />
          {isPolling && (
            <div className='absolute w-1.5 h-1.5 bg-green-500 rounded-full -top-0.5 -right-0.5 animate-pulse border border-white'></div>
          )}
        </div>
      </div>

      {!isProfessor && roomId && currentRoom?.resource_limits_enabled && (
        <>
          <Separator />
          <div className='px-1'>
            <PlayerResourceLimits roomId={roomId} />
          </div>
        </>
      )}

      <Separator />
      <div className='w-full h-[calc(100vh-280px)] flex flex-col justify-between'>
        <div className='flex flex-col gap-2'>
          {(currentRoom?.events.length ?? 0) > 1 && (
            <div className='mt-2 mb-2'>
              <GameRoomDescriptionDisplay
                text={currentRoom?.events[0]?.text || ''}
                events={currentRoom?.events || []}
                showViewAllOnly={true}
                eventGroupingOptions={eventGroupingOptions}
              />
            </div>
          )}

          {!hasVisibleEvents ? (
            <div className='py-8 text-center text-gray-500'>
              no current activity
            </div>
          ) : (
            <div className='flex flex-col gap-3'>
              {isProfessor && unreleased.length > 0 && (
                <Collapsible
                  key={UNRELEASED_GROUP_KEY}
                  open={openRounds[UNRELEASED_GROUP_KEY] ?? true}
                  onOpenChange={(isOpen) =>
                    onRoundToggle(UNRELEASED_GROUP_KEY, isOpen)
                  }
                  className='border-gray-200'
                >
                  <CollapsibleTrigger className='flex items-center gap-2 w-full h-9 px-3 rounded-md hover:bg-slate-100 group'>
                    <img
                      src='/icons/chevron-right.svg'
                      alt=''
                      className='w-3 transition-transform group-data-[state=open]:rotate-90'
                    />
                    <span className='text-sm font-semibold text-gray-700'>
                      Unreleased
                    </span>
                    <span className='text-xs font-medium text-slate-500'>
                      ({unreleased.length})
                    </span>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className='flex flex-col gap-2 pl-4 mt-1'>
                      {renderEventList(unreleased)}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}

              {roundGroups.map((group) => (
                <Collapsible
                  key={group.roundKey}
                  open={openRounds[group.roundKey] ?? true}
                  onOpenChange={(isOpen) =>
                    onRoundToggle(group.roundKey, isOpen)
                  }
                  className='border-gray-200'
                >
                  <CollapsibleTrigger className='flex items-center gap-2 w-full h-9 px-3 rounded-md hover:bg-slate-100 group'>
                    <img
                      src='/icons/chevron-right.svg'
                      alt=''
                      className='w-3 transition-transform group-data-[state=open]:rotate-90'
                    />
                    <span className='text-sm font-semibold text-gray-700'>
                      {group.roundLabel}
                    </span>
                    <span className='text-xs font-medium text-slate-500'>
                      ({group.events.length})
                    </span>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className='flex flex-col gap-2 pl-4 mt-1'>
                      {renderEventList(group.events, eventShowsPhaseLabel)}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
          )}
        </div>

        {user?.role == UserRole.Student && (
          <div className='mt-10'>
            {manualMode &&
              !myDecision?.decision &&
              roundStatus === ActivityState.Completed && (
                <div className='mb-2 text-orange-500'>
                  Decision will be marked late after game time is over
                </div>
              )}

            <SendDecisionModal
              isOpen={modalState.isOpen}
              mode={modalState.mode}
              onSend={handleSendDecision}
              onClose={closeModal}
              finalDecision={myDecision?.decision || ''}
              adjudication={myDecision?.adjudication || ''}
              currentUserId={user?.id}
              pastDecisions={pastDecisions}
            />

            <div className='flex gap-3'>
              {manualMode && (
                <Button
                  className='bg-dark-blue hover:bg-dark-blue-hovered'
                  onClick={() => openModal(getButtonMode())}
                >
                  {getButtonText()}
                </Button>
              )}

              <Button
                className='bg-dark-blue hover:bg-dark-blue-hovered'
                onClick={() => openModal(ModalMode.PAST_DECISIONS)}
                disabled={pastDecisions.length === 0}
              >
                Past Decisions
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default OGESidebarContent
